import { NextSeo } from 'next-seo';
import { useInView } from '../components/landing/hooks/useInView';
import Footer from '../components/landing/Footer';

const STATS = [
  { number: '100K+', label: 'Active Users', sub: 'and growing' },
  { number: '70+',   label: 'Active WebOns', sub: 'in ecosystem' },
  { number: '5+',    label: 'Chains Supported', sub: 'multi-chain' },
  { number: '1',     label: 'Unified Platform', sub: 'everything in one' },
];

const PROGRESS_BARS = [
  { label: 'User Growth',         milestone: '500K Users',  pct: 22 },
  { label: 'WebOn Adoption',       milestone: '150 WebOns',  pct: 48 },
  { label: 'Transaction Volume',   milestone: '$100M Vol.',  pct: 35 },
  { label: 'Partner Integrations', milestone: '50 Partners', pct: 34 },
];

const PERSONA_CARDS = [
  {
    for: 'For Users',
    title: 'Sovereign Control',
    items: [
      'Full asset control — self-custodial by design',
      'Reduced friction via Nomo-ID — one verification',
      'Unified Web3 + fiat connectivity experience',
      'Ownership without intermediary dependency',
      '70+ independent WebOns in one interface',
      'Direct dApp access, no extensions needed',
    ],
  },
  {
    for: 'For Developers',
    title: 'Build at Scale',
    items: [
      'Integrated SDK — all tools in one place',
      'API-first, modular architecture',
      'Immediate 100K+ user distribution',
      'Native wallet infrastructure included',
      'Decentralized authentication (Nomo-ID)',
      'Deploy WebOns in minutes',
    ],
  },
  {
    for: 'For Investors',
    title: 'Foundational Infrastructure',
    items: [
      'Intersection of DeFi, Web3 & real-world connectivity',
      'Bond Marketplace via partner · Coming Soon',
      'Open identity layer via Nomo-ID Protocol',
      'Cooperative structure = alignment',
      'Multiple revenue streams',
      'High switching costs — structurally defensible',
    ],
  },
];

function PageHeader() {
  const [ref, inView] = useInView();
  return (
    <section className="welcome section" id="why" ref={ref}>
      <div className="container">
        <div className={`reveal ${inView ? 'in-view' : ''}`}>
          <div className="section-label">Why NOMO</div>
          <h1 className="welcome__title">
            <span style={{ color: 'var(--white)' }}>Built for the</span>{' '}
            <span className="gradient-text">long game.</span>
          </h1>
          <p className="section-body" style={{ marginTop: '1.5rem', maxWidth: '720px' }}>
            NOMO is not a feature-based app. It is foundational Web3 infrastructure —
            positioned at the convergence of self-custodial assets, decentralized identity,
            and an open ecosystem of independent applications.
            Real traction. Defensible architecture. Compounding network effects.
          </p>
        </div>
      </div>
    </section>
  );
}

function Traction() {
  const [ref, inView] = useInView();
  return (
    <section className="traction section--dark" id="traction" ref={ref}>
      <div className="section-blobs" aria-hidden="true">
        <div className="blob blob--indigo" />
        <div className="blob blob--gold" />
      </div>

      <div className="container">
        <div className={`reveal ${inView ? 'in-view' : ''}`} style={{ textAlign: 'center' }}>
          <div className="section-label" style={{ justifyContent: 'center' }}>Traction</div>
          <h2 className="section-title section-title--lg" style={{ maxWidth: '640px', margin: '0 auto 1rem' }}>
            <span style={{ color: 'var(--white)' }}>Real users. Real infrastructure.</span>{' '}
            <span className="gradient-text">Real momentum.</span>
          </h2>
          <p className="section-body" style={{ margin: '0 auto 3rem', textAlign: 'center' }}>
            NOMO is not a whitepaper. It is live infrastructure with growing transaction volume,
            expanding partner integrations, and a scalable foundation.
          </p>
        </div>

        <div className="glass-wrap">
          <div className={`traction__grid stagger ${inView ? 'in-view' : ''}`}>
            {STATS.map((s) => (
              <div className="traction__stat" key={s.label}>
                <span className="traction__number">{s.number}</span>
                <span className="traction__label">{s.label}</span>
                <span className="traction__sub">{s.sub}</span>
              </div>
            ))}
          </div>

          <div
            className={`reveal ${inView ? 'in-view' : ''}`}
            style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '10px' }}
          >
            {PROGRESS_BARS.map((bar) => (
              <div key={bar.label} style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                <div style={{ fontFamily: 'var(--font-secondary)', fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--white-dim)', width: '180px', flexShrink: 0 }}>
                  {bar.label}
                </div>
                <div style={{ flex: 1, height: '2px', background: 'rgba(255,255,255,0.06)', position: 'relative', overflow: 'hidden' }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: inView ? `${bar.pct}%` : '0%',
                      background: 'linear-gradient(90deg, var(--gold-dark), var(--gold-light))',
                      transition: `width 1.2s cubic-bezier(0.4, 0, 0.2, 1) ${bar.pct * 5}ms`,
                    }}
                  />
                </div>
                <div style={{ fontFamily: 'var(--font-labels)', fontSize: '0.68rem', letterSpacing: '0.06em', color: 'var(--gold)', width: '90px', textAlign: 'right', flexShrink: 0, opacity: inView ? 1 : 0, transition: 'opacity 0.4s ease' }}>
                  {inView ? `→ ${bar.milestone}` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Personas() {
  const [ref, inView] = useInView();
  return (
    <section className="personas section" id="personas" ref={ref}>
      <div className="container">
        <div className={`reveal ${inView ? 'in-view' : ''}`} style={{ textAlign: 'center' }}>
          <div className="section-label" style={{ justifyContent: 'center' }}>Who NOMO Is For</div>
          <h2 className="section-title section-title--lg" style={{ maxWidth: '640px', margin: '0 auto 1rem' }}>
            <span style={{ color: 'var(--white)' }}>Built for everyone in the</span>{' '}
            <span className="gradient-text">decentralized economy.</span>
          </h2>
          <p className="section-body" style={{ margin: '0 auto 3rem', textAlign: 'center' }}>
            The decentralized economy is projected to grow exponentially over the next decade.
            NOMO is positioned as foundational infrastructure — not a feature-based app.
          </p>
        </div>

        <div className="glass-wrap">
          <div className={`personas__grid stagger ${inView ? 'in-view' : ''}`}>
            {PERSONA_CARDS.map((card) => (
              <div className="persona-card" key={card.for}>
                <div className="persona-card__for">{card.for}</div>
                <div className="persona-card__title">{card.title}</div>
                <div className="persona-card__items">
                  {card.items.map((item) => (
                    <div className="persona-item" key={item}>
                      <span className="persona-item__check">◆</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function InvestorCTA() {
  const [ref, inView] = useInView();
  return (
    <section className="cta-section" id="contact" ref={ref}>
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div className={`glass-wrap reveal ${inView ? 'in-view' : ''}`}>
          <div className="cta-section__eyebrow">Get in Touch</div>

          <h2 className="cta-section__title">
            <span style={{ color: 'var(--white)' }}>Interested in</span>
            <br />
            <span className="gradient-text">backing NOMO?</span>
          </h2>

          <p className="cta-section__sub">
            We're open to early-stage conversations with aligned partners and backers.
            Reach out directly and we'll get back to you.
          </p>

          <div className="hero__cta" style={{ justifyContent: 'center' }}>
            <a href="mailto:investment@nomo.app" className="btn btn-primary">
              <span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '8px', marginTop: '-2px' }}>
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polyline points="2,4 12,13 22,4" />
                </svg>
                investment@nomo.app
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Investors() {
  return (
    <>
      <NextSeo
        title="NOMO — For Investors"
        description="NOMO is foundational Web3 infrastructure. Real traction. Defensible architecture. Compounding network effects."
      />
      <main>
        <PageHeader />
        <Traction />
        <Personas />
        <InvestorCTA />
      </main>
      <Footer />
    </>
  );
}
